<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  tableId: { type: Number, required: true },
  columns: { type: Array, required: true },
});

const { t } = useI18n();
const tablesStore = useTablesStore();

const COLUMN_TYPES = [
  'text',
  'long_text',
  'number',
  'currency',
  'percent',
  'link',
  'email',
  'phone',
  'date',
  'datetime',
  'list',
  'multi_select',
  'checkbox',
];

const NUMERIC_TYPES = ['number', 'currency', 'percent'];
const SELECT_TYPES = ['list', 'multi_select'];

const draft = ref({ name: '', column_type: 'text', required: false, unique_constraint: false });
const optionsText = ref('');
const minValue = ref('');
const maxValue = ref('');
const errorMessage = ref('');

const buildConfig = () => {
  const config = {};
  if (NUMERIC_TYPES.includes(draft.value.column_type)) {
    if (minValue.value !== '') config.min = Number(minValue.value);
    if (maxValue.value !== '') config.max = Number(maxValue.value);
  }
  if (SELECT_TYPES.includes(draft.value.column_type)) {
    config.options = optionsText.value
      .split(',')
      .map(option => option.trim())
      .filter(Boolean);
  }
  return config;
};

const addColumn = async () => {
  if (!draft.value.name.trim()) return;
  errorMessage.value = '';
  try {
    await tablesStore.createColumn(props.tableId, {
      ...draft.value,
      name: draft.value.name.trim(),
      config: buildConfig(),
    });
    // A new column can widen the caller's writable-columns set (e.g. an
    // administrator's is derived from the live column list) — refresh the
    // cached table so the grid stops treating it as read-only.
    await tablesStore.fetchTable(props.tableId);
    draft.value = { name: '', column_type: 'text', required: false, unique_constraint: false };
    optionsText.value = '';
    minValue.value = '';
    maxValue.value = '';
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || t('TABLES.ERROR');
  }
};

// Only the display name, position and constraints are editable — the key is
// the column's identity and a bot resolves the column by it.
const renameColumn = async column => {
  const name = column.draftName?.trim();
  if (!name || name === column.name) return;
  await tablesStore.updateColumn(props.tableId, column.id, { name });
  await tablesStore.fetchTable(props.tableId);
};

const move = async (column, delta) => {
  await tablesStore.updateColumn(props.tableId, column.id, {
    position: (column.position || 0) + delta,
  });
  await tablesStore.fetchColumns(props.tableId);
  await tablesStore.fetchTable(props.tableId);
};

const removeColumn = async column => {
  errorMessage.value = '';
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TABLES.COLUMNS.CONFIRM_DELETE', { name: column.name }))) return;
  try {
    await tablesStore.deleteColumn(props.tableId, column.id);
    await tablesStore.fetchTable(props.tableId);
  } catch (error) {
    // The backend refuses deletion while a saved query or grant references
    // the column, and names them — surface that verbatim.
    errorMessage.value = error?.response?.data?.error || t('TABLES.ERROR');
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <p v-if="errorMessage" class="p-2 m-0 text-sm rounded text-n-ruby-11 bg-n-ruby-3">
      {{ errorMessage }}
    </p>

    <table class="w-full text-sm">
      <thead>
        <tr class="text-left text-n-slate-11">
          <th class="py-1">{{ t('TABLES.COLUMNS.NAME') }}</th>
          <th class="py-1">{{ t('TABLES.COLUMNS.KEY') }}</th>
          <th class="py-1">{{ t('TABLES.COLUMNS.TYPE') }}</th>
          <th class="py-1">{{ t('TABLES.COLUMNS.CONSTRAINTS') }}</th>
          <th class="py-1" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="column in columns" :key="column.id" class="border-t border-n-weak">
          <td class="py-1 pr-2">
            <Input
              :model-value="column.draftName ?? column.name"
              @update:model-value="column.draftName = $event"
              @blur="renameColumn(column)"
            />
          </td>
          <td class="py-1 pr-2">
            <code class="text-xs text-n-slate-11">{{ column.key }}</code>
          </td>
          <td class="py-1 pr-2 text-n-slate-11">
            {{ t(`TABLES.COLUMNS.TYPES.${column.column_type}`) }}
          </td>
          <td class="py-1 pr-2 text-xs text-n-slate-11">
            <span v-if="column.required">{{ t('TABLES.COLUMNS.REQUIRED') }}</span>
            <span v-if="column.unique_constraint"> · {{ t('TABLES.COLUMNS.UNIQUE') }}</span>
            <span v-if="column.config?.min !== undefined"> · min {{ column.config.min }}</span>
            <span v-if="column.config?.max !== undefined"> · max {{ column.config.max }}</span>
          </td>
          <td class="py-1 text-right whitespace-nowrap">
            <Button icon="i-lucide-arrow-up" size="xs" variant="ghost" @click="move(column, -1)" />
            <Button icon="i-lucide-arrow-down" size="xs" variant="ghost" @click="move(column, 1)" />
            <Button
              icon="i-lucide-trash-2"
              size="xs"
              variant="ghost"
              color="ruby"
              @click="removeColumn(column)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="flex flex-wrap items-end gap-2 pt-4 border-t border-n-weak">
      <Input
        v-model="draft.name"
        :label="t('TABLES.COLUMNS.NAME')"
        class="w-48"
        :placeholder="t('TABLES.COLUMNS.NAME_PLACEHOLDER')"
      />
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('TABLES.COLUMNS.TYPE') }}
        <select
          v-model="draft.column_type"
          class="px-2 py-1 text-sm border rounded border-n-weak bg-transparent text-n-slate-12"
        >
          <option v-for="type in COLUMN_TYPES" :key="type" :value="type">
            {{ t(`TABLES.COLUMNS.TYPES.${type}`) }}
          </option>
        </select>
      </label>
      <Input
        v-if="NUMERIC_TYPES.includes(draft.column_type)"
        v-model="minValue"
        :label="t('TABLES.COLUMNS.MIN')"
        type="number"
        class="w-24"
      />
      <Input
        v-if="NUMERIC_TYPES.includes(draft.column_type)"
        v-model="maxValue"
        :label="t('TABLES.COLUMNS.MAX')"
        type="number"
        class="w-24"
      />
      <Input
        v-if="SELECT_TYPES.includes(draft.column_type)"
        v-model="optionsText"
        :label="t('TABLES.COLUMNS.OPTIONS')"
        class="w-64"
        :placeholder="t('TABLES.COLUMNS.OPTIONS_PLACEHOLDER')"
      />
      <label class="flex items-center gap-1 text-xs text-n-slate-11">
        <input v-model="draft.required" type="checkbox" />
        {{ t('TABLES.COLUMNS.REQUIRED') }}
      </label>
      <label class="flex items-center gap-1 text-xs text-n-slate-11">
        <input v-model="draft.unique_constraint" type="checkbox" />
        {{ t('TABLES.COLUMNS.UNIQUE') }}
      </label>
      <Button :label="t('TABLES.COLUMNS.ADD')" size="sm" @click="addColumn" />
    </div>
  </div>
</template>
